<template>
  <div class="w-full">
    <!-- KSS Champion Statistics -->
    <div v-if="historyData" class="mb-6">
      <h3 class="text-gray-800 mb-4 text-lg sm:text-xl font-semibold">
        {{ historyTitle }}
      </h3>

      <div v-if="historyLoading" class="flex items-center justify-center py-6">
        <ProgressSpinner style="width: 24px; height: 24px" />
        <span class="ml-2 text-sm sm:text-base text-gray-600">
          {{ $t('DashboardComponent.loading') || 'Loading...' }}
          <br>
          {{ historyData }}
        </span>
      </div>

      <div
        v-else-if="historyError"
        class="text-red-600 text-sm sm:text-base text-center py-4"
      >
        <i class="pi pi-exclamation-circle mr-1"></i>
        {{ historyError }}
      </div>

      <div v-else class="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div class="flex items-center gap-3 bg-orange-50 rounded-lg p-3">
          <div class="bg-orange-100 text-orange-800 px-3 py-2 rounded-lg font-semibold text-lg">
            {{ historyData.kssChoosen || 0 }}x
          </div>
          <div>
            <i class="pi pi-heart text-gray-500 text-sm"></i>
            <span class="text-sm sm:text-base text-gray-700 ml-1">
              {{ $t('DashboardKssHistoryComponent.positiveFeedback') }}
            </span>
          </div>
        </div>

        <div class="flex items-center gap-3 bg-orange-50 rounded-lg p-3">
          <div class="bg-orange-100 text-orange-800 px-3 py-2 rounded-lg font-semibold text-lg">
            {{ historyData.kssTrackerCreated || 0 }}x
          </div>
          <div>
            <i class="pi pi-plus-circle text-gray-500 text-sm"></i>
            <span class="text-sm sm:text-base text-gray-700 ml-1">
              {{ $t('DashboardKssHistoryComponent.kssCreated') }}
            </span>
          </div>
        </div>

        <div class="flex items-center gap-3 bg-orange-50 rounded-lg p-3">
          <div class="bg-orange-100 text-orange-800 px-3 py-2 rounded-lg font-semibold text-lg">
            {{ historyData.kssTrackerSubmitted || 0 }}x
          </div>
          <div>
            <i class="pi pi-check-circle text-gray-500 text-sm"></i>
            <span class="text-sm sm:text-base text-gray-700 ml-1">
              {{ $t('DashboardKssHistoryComponent.kssSubmtted') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- KSS Trend Analysis -->
    <div v-if="meetings && meetings.length > 0">
      <h3 class="text-gray-800 mb-1 text-lg sm:text-xl font-semibold">
        {{ $t('DashboardComponent.kssTrendAnalysis') || 'Collaboration in the meeting' }}
      </h3>
      <p class="text-sm text-gray-500 mb-4">
        {{ $t('DashboardComponent.kssTrendSubtitle') || 'Evaluation of collaboration based on participant feedback' }}
      </p>

      <!-- KSS filters -->
      <div
        class="mb-4 flex flex-wrap items-start gap-x-4 gap-y-3 sm:items-center lg:flex-nowrap"
      >
        <!-- Time Period -->
        <div class="flex flex-wrap items-center gap-2">
          <span
            class="text-sm font-medium text-gray-700 whitespace-nowrap"
            :title="$t('TimePeriodSelector.timePeriodTooltip')"
          >
            {{ $t('TimePeriodSelector.timePeriod') || 'Time period' }}:
            <i class="pi pi-info-circle text-xs text-gray-400 ml-0.5"></i>
          </span>
          <select
            :value="meetingTimePeriod"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm
              focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            @change="emit('time-period-change', $event.target.value)"
          >
            <option value="all">{{ $t('TimePeriodSelector.allTime') || 'All Time' }}</option>
            <option value="week">{{ $t('TimePeriodSelector.lastWeek') || 'Last Week' }}</option>
            <option value="month">{{ $t('TimePeriodSelector.lastMonth') || 'Last Month' }}</option>
            <option value="quarter">{{ $t('TimePeriodSelector.lastQuarter') || 'Last Quarter' }}</option>
            <option value="year">{{ $t('TimePeriodSelector.lastYear') || 'Last Year' }}</option>
            <option value="custom">{{ $t('TimePeriodSelector.customRange') || 'Custom Range' }}</option>
          </select>
        </div>

        <!-- Custom Date Range (visible when "custom" selected) -->
        <div
          v-if="meetingTimePeriod === 'custom'"
          class="flex w-full flex-wrap items-center gap-2 sm:w-auto"
        >
          <label class="text-sm text-gray-700 whitespace-nowrap">
            {{ $t('TimePeriodSelector.fromDate') || 'From:' }}
          </label>
          <input
            :value="meetingCustomStart"
            type="date"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm
              focus:outline-none focus:ring-2 focus:ring-orange-500"
            @input="emit('update:meetingCustomStart', $event.target.value)"
          />
          <label class="text-sm text-gray-700 whitespace-nowrap">
            {{ $t('TimePeriodSelector.toDate') || 'To:' }}
          </label>
          <input
            :value="meetingCustomEnd"
            type="date"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm
              focus:outline-none focus:ring-2 focus:ring-orange-500"
            @input="emit('update:meetingCustomEnd', $event.target.value)"
          />
          <button
            class="btn-primary-accent px-4 py-2 text-sm whitespace-nowrap"
            @click="emit('apply-custom-range')"
          >
            {{ $t('TimePeriodSelector.apply') || 'Apply' }}
          </button>
        </div>

        <!-- Meeting Selector -->
        <div
          class="flex w-full flex-wrap items-center gap-2 sm:w-auto
            sm:border-l sm:border-gray-200 sm:pl-3 sm:ml-1"
        >
          <span
            class="text-sm font-medium text-gray-700 whitespace-nowrap"
            :title="$t('TimePeriodSelector.selectMeetingTooltip')"
          >
            {{ $t('TimePeriodSelector.selectMeeting') || 'Select meeting' }}
            <i class="pi pi-info-circle text-xs text-gray-400 ml-0.5"></i>
          </span>
          <Select
            v-model="selectedMeeting"
            :options="meetings"
            :optionLabel="meetingLabel"
            :placeholder="$t('TimePeriodSelector.selectMeeting') || 'Select meeting'"
            class="w-fit max-w-full min-w-0"
            @change="onMeetingSelect"
          />
        </div>

        <!-- Occurrence Date Filter (recurring meetings only) -->
        <div
          v-if="selectedMeetingObj?.isRecurring && rawOccurrences
            && rawOccurrences.length > 1"
          class="flex w-full flex-wrap items-center gap-2 sm:w-auto
            sm:border-l sm:border-gray-200 sm:pl-3 sm:ml-1"
        >
          <span
            class="text-sm font-medium text-gray-700 whitespace-nowrap"
            :title="$t('TimePeriodSelector.filterOccurrencesTooltip')"
          >
            {{ $t('TimePeriodSelector.filterOccurrences') || 'Narrow down time period' }}:
            <i class="pi pi-info-circle text-xs text-gray-400 ml-0.5"></i>
          </span>
          <label class="text-sm text-gray-700 whitespace-nowrap">
            {{ $t('TimePeriodSelector.fromDate') || 'From:' }}
          </label>
          <input
            :value="occurrenceCustomStart"
            type="date"
            :min="occurrenceDateRange.min"
            :max="occurrenceCustomEnd || occurrenceDateRange.max"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm
              focus:outline-none focus:ring-2 focus:ring-orange-500"
            @input="emit('update:occurrenceCustomStart', $event.target.value)"
            @change="emit('occurrence-date-change')"
          />
          <label class="text-sm text-gray-700 whitespace-nowrap">
            {{ $t('TimePeriodSelector.toDate') || 'To:' }}
          </label>
          <input
            :value="occurrenceCustomEnd"
            type="date"
            :min="occurrenceCustomStart || occurrenceDateRange.min"
            :max="occurrenceDateRange.max"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm
              focus:outline-none focus:ring-2 focus:ring-orange-500"
            @input="emit('update:occurrenceCustomEnd', $event.target.value)"
            @change="emit('occurrence-date-change')"
          />
          <button
            v-if="occurrenceCustomStart || occurrenceCustomEnd"
            class="shrink-0 px-3 py-2 text-sm text-gray-600 hover:text-gray-800
              border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
            @click="emit('reset-occurrence-filter')"
          >
            {{ $t('TimePeriodSelector.reset') || 'Reset' }}
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="trendLoading" class="flex items-center justify-center py-6">
        <ProgressSpinner style="width: 24px; height: 24px" />
        <span class="ml-2 text-sm sm:text-base text-gray-600">
          {{ $t('DashboardComponent.loading') || 'Loading...' }}
        </span>
      </div>

      <!-- Error -->
      <div
        v-else-if="trendError"
        class="text-red-600 text-sm sm:text-base text-center py-4"
      >
        <i class="pi pi-exclamation-circle mr-1"></i>
        {{ trendError }}
      </div>

      <!-- No Data -->
      <div
        v-else-if="!trendPlotData"
        class="text-gray-500 text-sm sm:text-base text-center py-4"
      >
        {{ $t('DashboardComponent.notEnoughDataAvailable') || 'Not enough data available' }}
      </div>

      <!-- Trend Chart -->
      <div v-else class="w-full">
        <div
          v-if="trendBoxplotConfig"
          class="flex items-center justify-end mb-2"
        >
          <label class="flex items-center cursor-pointer text-sm text-gray-600 gap-2">
            <span>{{ $t('MyResultsDashboardComponent.showDistribution') || 'Show distribution' }}</span>
            <ToggleSwitch
              :modelValue="showBoxplot"
              @update:modelValue="(v) => emit('update:showBoxplot', v)"
            />
          </label>
        </div>
        <div class="w-full h-[300px] lg:h-[400px] flex flex-col min-h-0">
          <div class="flex-1 min-h-0">
            <BoxPlotComponent
              v-if="trendPlotData.type === 'boxplot'"
              :chart-config="trendPlotData"
              :dimensions="dimensions"
            />
            <BarChartComponent
              v-else
              :chart-config="trendPlotData"
              :dimensions="dimensions"
            />
          </div>
          <LikertScaleLegend
            v-if="trendUsesLikertScale"
            variant="belowChart"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatLocaleDate } from '@/helpers/formatLocaleDate';
import ProgressSpinner from 'primevue/progressspinner';
import Select from 'primevue/select';
import ToggleSwitch from 'primevue/toggleswitch';
import BarChartComponent from './BarChartComponent.vue';
import BoxPlotComponent from './BoxPlotComponent.vue';
import LikertScaleLegend from './LikertScaleLegend.vue';

const props = defineProps({
  historyData: { type: Object, default: null },
  historyLoading: { type: Boolean, default: false },
  historyError: { type: String, default: null },
  historyTitle: { type: String, default: '' },
  meetings: { type: Array, default: () => [] },
  trendPlotData: { type: Object, default: null },
  trendBoxplotConfig: { type: Object, default: null },
  showBoxplot: { type: Boolean, default: false },
  trendLoading: { type: Boolean, default: false },
  trendError: { type: String, default: null },
  dimensions: { type: Array, default: () => [] },
  meetingTimePeriod: { type: String, default: 'all' },
  meetingCustomStart: { type: String, default: '' },
  meetingCustomEnd: { type: String, default: '' },
  selectedMeetingObj: { type: Object, default: null },
  rawOccurrences: { type: Array, default: null },
  occurrenceDateRange: { type: Object, default: () => ({ min: '', max: '' }) },
  occurrenceCustomStart: { type: String, default: '' },
  occurrenceCustomEnd: { type: String, default: '' },
});

const emit = defineEmits([
  'meeting-select',
  'update:showBoxplot',
  'time-period-change',
  'apply-custom-range',
  'update:meetingCustomStart',
  'update:meetingCustomEnd',
  'update:occurrenceCustomStart',
  'update:occurrenceCustomEnd',
  'occurrence-date-change',
  'reset-occurrence-filter',
]);

const { locale } = useI18n();
const selectedMeeting = ref(null);

const trendUsesLikertScale = computed(() => {
  const y = props.trendPlotData?.options?.scales?.y;
  if (!y || y.min !== 0) return false;
  const max = y.max ?? y.suggestedMax;
  return max === 5 || max === 6;
});

function meetingLabel(meeting) {
  if (!meeting) return '';
  const name = meeting.name || '';
  const date = meeting.date_assessmentMade
    ? formatLocaleDate(meeting.date_assessmentMade, locale.value)
    : '';
  return `${name} - ${date}`;
}

function onMeetingSelect() {
  if (selectedMeeting.value) {
    emit('meeting-select', selectedMeeting.value);
  }
}

// Auto-select first meeting
watch(
  () => props.meetings,
  (newMeetings) => {
    if (newMeetings?.length > 0 && !selectedMeeting.value) {
      const [first] = newMeetings;
      selectedMeeting.value = first;
    }
  },
  { immediate: true },
);
</script>
